import React, { useState } from 'react';

const AnchorSVG = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="20" cy="7" r="2" fill="currentColor"/>
    <line x1="4" y1="14" x2="36" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="20" y1="12" x2="20" y2="40" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 40 C13 38 5 33 5 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M20 40 C27 38 35 33 35 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M5 26 C5 46 35 46 35 26" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

const Afterparty: React.FC = () => {
  const [mapOpen, setMapOpen] = useState(false);

  return (
    <main>
      {/* PAGE HEADER */}
      <div className="page-header">
        <AnchorSVG className="page-header__anchor" />
        <p className="page-header__eyebrow">Keep the Celebration Going</p>
        <h1 className="page-header__title">Afterparty</h1>
        <div className="page-header__rule page-rule">
          <div className="rule-line rule-line--short"></div>
          <div className="rule-diamond"></div>
          <div className="rule-line rule-line--short"></div>
        </div>
      </div>

      <div className="details-content">
        <div className="details-inner">

          {/* INTRO */}
          <div className="detail-section">
            <p className="venue-card__desc" style={{ textAlign: 'left' }}>
              We are super excited to see you at our wedding! We will be holding an
              afterparty beginning at 4:30 PM with snacks, drinks, and yard games to keep
              the celebration going. No dress code &mdash; come dressed comfortably.
            </p>
          </div>

          {/* DIVIDER */}
          <div className="section-divider page-rule">
            <div className="rule-line rule-line--flex"></div>
            <div className="rule-diamond"></div>
            <div className="rule-line rule-line--flex"></div>
          </div>

          {/* LOCATION */}
          <div className="detail-section">
            <p className="section-eyebrow detail-section__eyebrow">Location</p>
            <p className="venue-card__desc" style={{ textAlign: 'left' }}>
              The afterparty will be held at Lake St. Clair Metropark, Huron Point Picnic Shelter.
              There is shuttle service within the park that guests can take to the picnic shelter.
              On the map below, the picnic shelter is marked by the letter <strong>J</strong>.
            </p>
            <div className="venue-card">
              <p className="venue-card__name">Lake St. Clair Metropark</p>
              <p className="venue-card__address">
                Huron Point Picnic Shelter<br />
                31300 Metropolitan Pkwy.<br />
                Harrison Township, MI 48045
              </p>
              <a
                className="venue-card__link"
                href="https://www.google.com/maps/dir/?api=1&destination=31300+Metropolitan+Pkwy+Harrison+Township+MI+48045"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Directions &rarr;
              </a>
            </div>
          </div>

          {/* DIVIDER */}
          <div className="section-divider page-rule">
            <div className="rule-line rule-line--flex"></div>
            <div className="rule-diamond"></div>
            <div className="rule-line rule-line--flex"></div>
          </div>

          {/* GETTING THERE */}
          <div className="detail-section">
            <p className="section-eyebrow detail-section__eyebrow">Getting There</p>
            <p className="venue-card__desc" style={{ textAlign: 'left' }}>
              The park is about a <strong>22 minute drive</strong> from the wedding venue &mdash;
              we recommend carpooling to make it easier.
            </p>
            <p className="venue-card__desc" style={{ textAlign: 'left', marginTop: '1rem' }}>
              The park has an entrance fee, but you <strong>DO NOT</strong> have to pay.
              We&rsquo;ve given the park a list of every guest attending, so you&rsquo;ll be
              covered at the gate. When you pull up, just tell the attendant:
            </p>
            <ul className="afterparty-list" style={{ textAlign: 'left', margin: '1rem 0 0 1.5rem', padding: 0 }}>
              <li style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: '0.4rem' }}>
                Your name (whoever is driving)
              </li>
              <li style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.8 }}>
                That you&rsquo;re here for an event and to check the &ldquo;post-pay guest list&rdquo;
              </li>
            </ul>
            <p style={{ textAlign: 'left', color: 'var(--gold)', fontSize: '0.65rem', letterSpacing: '3px', textTransform: 'uppercase', margin: '1.25rem 0 0.75rem' }}>
              &mdash; or &mdash;
            </p>
            <p className="venue-card__desc" style={{ textAlign: 'left' }}>
              If you already have a valid park pass, you can use that to get in instead.
            </p>
            <p className="venue-card__desc" style={{ textAlign: 'left', marginTop: '1rem' }}>
              Either way, they&rsquo;ll wave you right through.
            </p>
          </div>

          {/* DIVIDER */}
          <div className="section-divider page-rule">
            <div className="rule-line rule-line--flex"></div>
            <div className="rule-diamond"></div>
            <div className="rule-line rule-line--flex"></div>
          </div>

          {/* ONCE INSIDE */}
          <div className="detail-section">
            <p className="section-eyebrow detail-section__eyebrow">Once You&rsquo;re In the Park</p>
            <p className="venue-card__desc" style={{ textAlign: 'left' }}>
              Follow these steps to get from the park entrance to the shelter:
            </p>
            <ol className="afterparty-steps" style={{ textAlign: 'left', margin: '1.25rem 0 0 1.5rem', padding: 0 }}>
              <li style={{ color: 'var(--muted)', fontSize: '0.95rem', lineHeight: 1.9, marginBottom: '0.5rem' }}>
                Follow signs to the <strong>South Marina</strong>
              </li>
              <li style={{ color: 'var(--muted)', fontSize: '0.95rem', lineHeight: 1.9, marginBottom: '0.5rem' }}>
                Park in <strong>Parking Lot D</strong>
              </li>
              <li style={{ color: 'var(--muted)', fontSize: '0.95rem', lineHeight: 1.9, marginBottom: '0.5rem' }}>
                Board the shuttle at the <strong>South Marina</strong>
              </li>
              <li style={{ color: 'var(--muted)', fontSize: '0.95rem', lineHeight: 1.9, marginBottom: '0.5rem' }}>
                Get off at the <strong>Huron Point</strong> shuttle stop
              </li>
              <li style={{ color: 'var(--muted)', fontSize: '0.95rem', lineHeight: 1.9 }}>
                Walk to <strong>Shelter J</strong>
              </li>
            </ol>
          </div>

          {/* DIVIDER */}
          <div className="section-divider page-rule">
            <div className="rule-line rule-line--flex"></div>
            <div className="rule-diamond"></div>
            <div className="rule-line rule-line--flex"></div>
          </div>

          {/* MAP */}
          <div className="detail-section">
            <p className="section-eyebrow detail-section__eyebrow">Park Map</p>
            <p style={{ textAlign: 'left', color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: '1.5rem' }}>
              Use the map below as a reference. Tap to enlarge.
            </p>
            <img
              src={`${process.env.PUBLIC_URL}/LakeStClairParkMap.png`}
              alt="Lake St. Clair Metropark map"
              onClick={() => setMapOpen(true)}
              style={{
                width: '100%',
                maxWidth: '700px',
                display: 'block',
                margin: '0 auto',
                borderRadius: '4px',
                cursor: 'zoom-in',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.02)';
                (e.currentTarget as HTMLImageElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';
                (e.currentTarget as HTMLImageElement).style.boxShadow = 'none';
              }}
            />
            <p style={{ textAlign: 'center', marginTop: '1.25rem' }}>
              <a
                href={`${process.env.PUBLIC_URL}/LakeStClairParkMap.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="venue-card__link"
              >
                Download PDF Map &rarr;
              </a>
            </p>
          </div>

          <div className="detail-note">
            <p>
              If you have any questions, please reach out to us directly. See you there!
            </p>
          </div>

        </div>
      </div>

      {mapOpen && (
        <div
          onClick={() => setMapOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(30,24,16,0.85)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out',
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/LakeStClairParkMap.png`}
            alt="Lake St. Clair Metropark map"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '95vw', maxHeight: '95vh', borderRadius: '4px' }}
          />
        </div>
      )}
    </main>
  );
};

export default Afterparty;
